import { formatDistanceToNowStrict } from 'date-fns';
import type { ActivityEvent, ActivityMinute } from '../../services/team-activity.service';

export const AREAS: { key: string; label: string; color: string }[] = [
  { key: 'reporting', label: 'Reporting', color: '#2a78d6' },
  { key: 'dashboards', label: 'Dashboards', color: '#06b6d4' },
  { key: 'ads_manager', label: 'Ads Manager', color: '#e0a400' },
  { key: 'funnel', label: 'Funnel setup', color: '#0ca30c' },
  { key: 'creatives', label: 'Creatives', color: '#ec4899' },
  { key: 'admin', label: 'Admin', color: '#8b5cf6' },
  { key: 'other', label: 'Other', color: '#94a3b8' },
];
export const DEVICES: { key: string; label: string; color: string }[] = [
  { key: 'desktop', label: 'Desktop', color: '#2a78d6' },
  { key: 'tablet', label: 'Tablet', color: '#e0a400' },
  { key: 'phone', label: 'Phone', color: '#d03b3b' },
];
const byKey = (list: { key: string; label: string; color: string }[]) => new Map(list.map((x) => [x.key, x]));
const AREA_BY = byKey(AREAS);
const DEVICE_BY = byKey(DEVICES);
export const areaLabel = (k: string) => AREA_BY.get(k)?.label ?? k;
export const areaColor = (k: string) => AREA_BY.get(k)?.color ?? '#94a3b8';
export const deviceLabel = (k: string) => DEVICE_BY.get(k)?.label ?? (k || 'Unknown');
export const deviceColor = (k: string) => DEVICE_BY.get(k)?.color ?? '#94a3b8';
export type ColorBy = 'area' | 'device';
export const colorOf = (by: ColorBy, m: ActivityMinute) => (by === 'area' ? areaColor(m.area) : deviceColor(m.device));
export const categoryOf = (by: ColorBy, m: ActivityMinute) => (by === 'area' ? m.area : m.device);

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const fmtDuration = (minutes: number): string => {
  const m = Math.round(minutes);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
};
export const fmtClock = (minuteOfDay: number): string =>
  `${String(Math.floor(minuteOfDay / 60)).padStart(2, '0')}:${String(Math.round(minuteOfDay) % 60).padStart(2, '0')}`;

// The server reads every hour in Israel time, whoever is looking.
export const TEAM_TZ = 'Asia/Jerusalem';
export const inTeamTime = (iso: string) => new Date(iso).toLocaleString('sv-SE', { timeZone: TEAM_TZ, hour12: false });
/** Today's date in Israel, as a local midnight, so a period ends on Israel's day, not the browser's. */
export const teamToday = (now = new Date()) => {
  const [y, m, d] = inTeamTime(now.toISOString()).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
};

// ClickHouse returns UTC wall-clock without a zone.
export const utc = (s: string) => new Date(`${s.replace(' ', 'T')}Z`);
export const ago = (s: string | null) => (s ? `${formatDistanceToNowStrict(utc(s))} ago` : '—');
export const ONLINE_MS = 10 * 60_000;
export const isOnline = (lastActive: string | null) => !!lastActive && Date.now() - utc(lastActive).getTime() < ONLINE_MS;

/** Monday 0 .. Sunday 6, for a YYYY-MM-DD date. */
export const weekdayOf = (date: string) => (new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7;
const minuteOfDay = (t: string) => Number(t.slice(11, 13)) * 60 + Number(t.slice(14, 16));

export function daysOf(from: string, to: string): string[] {
  const out: string[] = [];
  const end = new Date(`${to.slice(0, 10)}T00:00:00Z`);
  for (let d = new Date(`${from.slice(0, 10)}T00:00:00Z`); d <= end && out.length < 400; d.setUTCDate(d.getUTCDate() + 1)) {
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

export interface Filters { areas: string[]; devices: string[] }
export const passes = (f: Filters, m: { area: string; device?: string }) =>
  (!f.areas.length || f.areas.includes(m.area)) && (!f.devices.length || !m.device || f.devices.includes(m.device));

export function heatGrid(items: { t: string }[]): number[] {
  const grid = new Array(7 * 24).fill(0);
  for (const it of items) grid[weekdayOf(it.t.slice(0, 10)) * 24 + Number(it.t.slice(11, 13))] += 1;
  return grid;
}

export function heatDominant(minutes: ActivityMinute[], by: ColorBy): (string | null)[] {
  const counts = Array.from({ length: 7 * 24 }, () => new Map<string, number>());
  for (const m of minutes) {
    const cell = counts[weekdayOf(m.t.slice(0, 10)) * 24 + Number(m.t.slice(11, 13))];
    const k = categoryOf(by, m);
    cell.set(k, (cell.get(k) ?? 0) + 1);
  }
  return counts.map((c) => [...c.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null);
}

export const SESSION_GAP = 10;

/** A day's minutes as coloured runs; a gap of more than two minutes, or a new category, starts a new run. */
export function dayRuns(minutes: ActivityMinute[], by: ColorBy) {
  const runs: { start: number; end: number; category: string; first: ActivityMinute }[] = [];
  for (const m of minutes) {
    const at = minuteOfDay(m.t);
    const cat = categoryOf(by, m);
    const last = runs[runs.length - 1];
    if (last && last.category === cat && at - last.end <= 2) last.end = at;
    else runs.push({ start: at, end: at, category: cat, first: m });
  }
  return runs;
}

const median = (xs: number[]) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  return s.length % 2 ? s[(s.length - 1) / 2] : (s[s.length / 2 - 1] + s[s.length / 2]) / 2;
};

export function rhythm(minutes: ActivityMinute[]) {
  const perDay = new Map<string, number[]>();
  for (const m of minutes) {
    const d = m.t.slice(0, 10);
    perDay.set(d, [...(perDay.get(d) ?? []), minuteOfDay(m.t)]);
  }
  let sessions = 0;
  let longest = 0;
  const starts: number[] = [];
  const ends: number[] = [];
  let weekend = 0;
  for (const [d, mins] of perDay) {
    mins.sort((a, b) => a - b);
    starts.push(mins[0]);
    ends.push(mins[mins.length - 1]);
    if (weekdayOf(d) >= 5) weekend += mins.length;
    let runStart = mins[0];
    for (let i = 0; i <= mins.length; i++) {
      if (i === mins.length || (i > 0 && mins[i] - mins[i - 1] > SESSION_GAP)) {
        sessions += 1;
        longest = Math.max(longest, mins[i - 1] - runStart + 1);
        if (i < mins.length) runStart = mins[i];
      }
    }
  }
  const total = minutes.length;
  return {
    total, days: perDay.size, sessions, longest,
    perActiveDay: perDay.size ? total / perDay.size : 0,
    typicalStart: median(starts), typicalEnd: median(ends),
    weekendShare: total ? weekend / total : 0,
  };
}

export function mixByDay(minutes: ActivityMinute[], days: string[], by: ColorBy) {
  const rows = new Map(days.map((d) => [d, { day: d.slice(5) } as Record<string, number | string>]));
  for (const m of minutes) {
    const row = rows.get(m.t.slice(0, 10));
    if (!row) continue;
    const k = categoryOf(by, m);
    row[k] = Number(row[k] ?? 0) + 1;
  }
  return [...rows.values()];
}

export const eventLabel = (e: ActivityEvent) => (e.channel === 'mcp'
  ? `MCP ${e.kind}: ${e.tool.replace(/_/g, ' ')}${e.entity_type ? ` (${e.entity_type.replace(/_/g, ' ')})` : ''}${e.mcp_client ? ` · ${e.mcp_client}` : ''}`
  : `Changed ${e.entity_type.replace(/_/g, ' ')} (${e.page})`);
